import { FilterValue } from '../../../shared-component/app-filter-select/app-filter-select.component';

/**
 * Resolves the `role` request param for the four role-scoped dropdown endpoints
 * (`get-user-list`, `get-department-list`, `locations`, `get-license-types-by-flag`).
 *
 * These endpoints scope their lists by the caller's role, and until now every one of them
 * was called with the signed-in user's SYSTEM role regardless of what the page's Role filter
 * (or the Dashboard's role view) said — so a Department Head saw options describing their own
 * scope beside rows describing the department's.
 */

/** The `get-user-license-roles` id for Department Head — Role filter option and
    Management-dashboard variant alike. */
export const DEPARTMENT_HEAD_ROLE_ID = 'DEPT';

/**
 * The system role Performer and Reviewer are scoped under.
 *
 * Same contract as `PERFORMER_REVIEWER_API_SYSTEM_ROLE` in
 * `performer-reviewer-dashboard.component.ts`, which pairs this exact value with a
 * `role_flag` of `'P'`/`'R'` on `get-workspace-license-counts`. That constant is deliberately
 * NOT re-pointed here: it guards a different endpoint's payload and has its own drift test,
 * and duplicating four characters across two files that cross-reference each other is
 * lower-risk than editing a guarded contract for tidiness.
 */
export const EXECUTIVE_SYSTEM_ROLE = 'EXCT';

/** Role-filter option ids that scope under {@link EXECUTIVE_SYSTEM_ROLE}. */
const EXECUTIVE_SCOPED_ROLE_IDS: readonly string[] = ['P', 'R'];

/**
 * Is the Role filter set to Performer (`'P'`) or Reviewer (`'R'`)?
 *
 * The filter-driven grid pages (Workspace, Documents · License Documents, Reports) HIDE their
 * User filter for these two roles: both scope the grid to the signed-in user's own work, so
 * offering a User dropdown alongside implies a choice the role does not have. Every other
 * selection — `MGMT`, `DEPT`, `AUDT`, `CADMN`, or none at all — keeps it.
 *
 * Shares {@link EXECUTIVE_SCOPED_ROLE_IDS} with {@link resolveDropdownRoleScope} so the two
 * answers about the same pair of role ids cannot drift, and normalises casing/whitespace for
 * the same reason it does: the value originates in an API payload.
 *
 * @param selectedRole The Role filter's current value (`store.filters().role`).
 * @returns `true` when the selection is Performer or Reviewer.
 */
export function isPerformerOrReviewerRole(selectedRole: FilterValue | undefined): boolean {
  return EXECUTIVE_SCOPED_ROLE_IDS.includes(String(selectedRole ?? '').trim().toUpperCase());
}

/**
 * Maps a Role-filter selection (or Dashboard role view) onto the `role` param the four
 * scope-sensitive dropdown endpoints should be called with.
 *
 * Department Head scopes as itself, Performer/Reviewer scope as the executive role, and every
 * other selection — `MGMT`, `AUDT`, `CADMN`, or none at all — keeps the signed-in user's
 * system role. Casing and surrounding whitespace are normalised because the value originates
 * in an API payload.
 *
 * Takes the raw {@link FilterValue} a Role filter holds, so a page passes
 * `store.filters().role` straight through and needs no coercion helper of its own.
 *
 * @param selectedRole The Role filter's current value / the Dashboard's role view id.
 * @param systemRole The signed-in user's system role (`UserService.getRole()`).
 * @returns The `role` to send.
 */
export function resolveDropdownRoleScope(
  selectedRole: FilterValue | undefined,
  systemRole: string,
): string {
  const id = String(selectedRole ?? '').trim().toUpperCase();
  if (id === DEPARTMENT_HEAD_ROLE_ID) return DEPARTMENT_HEAD_ROLE_ID;
  if (EXECUTIVE_SCOPED_ROLE_IDS.includes(id)) return EXECUTIVE_SYSTEM_ROLE;
  return systemRole;
}
